import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { loginUser, clearError } from "../../features/auth/authSlice";
import "./login.css";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { token, loading, error } = useSelector((state) => state.auth);

  const from = location.state?.from?.pathname || "/dashboard";

  // Single source of truth for navigation: whenever a token is present
  // (fresh login or an already-authenticated user landing on the login page),
  // redirect to the intended route. The success toast lives only in
  // handleSubmit so it fires once, and only on an explicit login action.
  useEffect(() => {
    if (token || localStorage.getItem("token")) {
      navigate(from, { replace: true });
    }
  }, [token, navigate, from]);

  useEffect(() => {
    if (error) {
      toast.error(error.toString(), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await dispatch(loginUser(formData)).unwrap();

      toast.success("Login successful!", {
        position: "top-right",
        autoClose: 1000,
      });
      // Navigation is handled by the token effect above — no navigate here,
      // otherwise it fires twice.
    } catch (error) {}
  };

  return (
    <main id="login" className="py-5">
      <div className="our-container">
        <div className="inner-container">
          <div className="login-block">
            <div className="login-header-block">
              <div className="login-tab-section">
                <div className="login-link-aTag">Login</div>
              </div>
            </div>
            <div className="login-body-block">
              <form onSubmit={handleSubmit}>
                <div className="login-email-group mb-5">
                  <label>
                    <span className="text-danger">* </span>Email
                  </label>
                  <div className="login-email-field">
                    <input
                      type="email"
                      className={`form-control ${
                        errors.email ? "is-invalid" : ""
                      }`}
                      placeholder="Enter your email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    {errors.email && (
                      <div className="invalid-feedback d-block">
                        {errors.email}
                      </div>
                    )}
                  </div>
                </div>

                <div className="login-password-group mb-5">
                  <label>
                    <span className="text-danger">* </span>Password
                  </label>
                  <div className="login-password-field">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`form-control ${
                        errors.password ? "is-invalid" : ""
                      }`}
                      placeholder="Enter your password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                      style={{ paddingRight: "2.75rem" }}
                    />
                    <button
                      type="button"
                      className="position-absolute top-50 translate-middle-y"
                      style={{
                        right: "0.85rem",
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        zIndex: 5,
                        color: "#6c757d",
                        lineHeight: 0,
                      }}
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                    {errors.password && (
                      <div className="invalid-feedback d-block">
                        {errors.password}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Login...
                    </>
                  ) : (
                    "Login"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
